// frontend/src/components/Notifications/Notifications.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, ListGroup, Badge, Button, Spinner, } from 'react-bootstrap';
import { FaBell, FaCheck } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';

const Notifications = () => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/notifications/');
            const notifs = response.data.notifications.data || [];
            setNotifications(notifs);
        } catch (err) {
            console.error('Error loading notifications:', err);
            toast.error('Erreur lors du chargement des notifications');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadNotifications();
    }, [loadNotifications]);

    const markAsRead = async (id) => {
        try {
            await api.put(`/notifications/${id}/read`);
            loadNotifications();
            toast.success('Notification marquée comme lue');
        } catch (err) {
            console.error('Error marking notification as read:', err);
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const markAllAsRead = async () => {
        try {
            await api.post('/notifications/mark-all-read');
            loadNotifications();
            toast.success('Toutes les notifications ont été marquées comme lues');
        } catch (err) {
            console.error('Error marking all as read:', err);
            toast.error('Erreur lors de la mise à jour');
        }
    };



    const handleNotificationClick = (notification) => {
        if (!notification.is_read) {
            markAsRead(notification.id);
        }
        
        if (notification.link) {
            navigate(notification.link);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) {
            return 'Aujourd\'hui ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays === 1) {
            return 'Hier ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays < 7) {
            return `Il y a ${diffDays} jours`;
        } else {
            return date.toLocaleDateString('fr-FR');
        }
    };

    const unreadCount = notifications.filter(n => !n.is_read).length;

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Chargement des notifications...</p>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>
                    <FaBell className="me-2" />
                    Notifications
                    {unreadCount > 0 && (
                        <Badge bg="danger" className="ms-2">{unreadCount}</Badge>
                    )}
                </h2>
                {unreadCount > 0 && (
                    <Button variant="outline-primary" size="sm" onClick={markAllAsRead}>
                        <FaCheck className="me-1" />
                        Tout marquer comme lu
                    </Button>
                )}
            </div>

            {notifications.length === 0 ? (
                <Card className="shadow-sm border-0">
                    <Card.Body className="text-center py-5">
                        <FaBell size={50} className="text-muted mb-3" />
                        <h5>Aucune notification</h5>
                        <p className="text-muted">Vous n'avez aucune notification pour le moment.</p>
                    </Card.Body>
                </Card>
            ) : (
                <Card className="shadow-sm border-0">
                    <ListGroup variant="flush">
                        {notifications.map((notification) => (
                            <ListGroup.Item
                                key={notification.id}
                                className={`notification-item ${!notification.is_read ? 'bg-light' : ''}`}
                                style={{ cursor: notification.link ? 'pointer' : 'default' }}
                            >
                                <div className="d-flex">
                                    <div className="flex-grow-1" onClick={() => handleNotificationClick(notification)}>
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <h6 className="mb-0">
                                                {!notification.is_read && (
                                                    <span className="badge bg-primary me-2" style={{ fontSize: '8px', padding: '4px' }}>●</span>
                                                )}
                                                {notification.title}
                                            </h6>
                                            <small className="text-muted">{formatDate(notification.created_at)}</small>
                                        </div>
                                        <p className="mb-0 text-muted">{notification.message}</p>
                                    </div>
                                    <div className="ms-3 d-flex gap-2">
                                        {!notification.is_read && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-success p-0"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    markAsRead(notification.id);
                                                }}
                                                title="Marquer comme lu"
                                            >
                                                <FaCheck />
                                            </Button>
                                        )}
                                        {/* <Button
                                            variant="link"
                                            size="sm"
                                            className="text-danger p-0"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteNotification(notification.id);
                                            }}
                                            title="Supprimer"
                                        >
                                            <FaTrash />
                                        </Button> */}
                                    </div>
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </Card>
            )}
        </Container>
    );
};

export default Notifications;